<template>
	<div class="container py-4">
		<!-- Header -->
		<div class="d-flex align-items-center mb-4">
			<RouterLink
				class="btn btn-outline-secondary me-3"
				to="/_/configuration/integration"
				title="Back to integrations"
			>
				<i class="bi bi-arrow-left"></i>
			</RouterLink>
			<div>
				<h1 class="mb-0">VeeMac</h1>
				<p class="text-muted mb-0">
					Manage your VeeMac credentials and synchronization
				</p>
			</div>
		</div>

		<!-- VeeMac Integration -->
		<div class="card mb-4 integration-card veemac">
			<div
				class="card-header d-flex justify-content-between align-items-center"
			>
				<div>
					<h2 class="h5 mb-0">VeeMac</h2>
				</div>
				<img
					src="https://via.placeholder.com/100x40?text=VeeMac"
					alt="VeeMac Logo"
					height="40"
				/>
			</div>
			<div class="card-body">
				<div class="table-responsive mb-3">
					<table class="table table-borderless">
						<tbody>
							<tr>
								<td width="30%"><strong>Username</strong></td>
								<td>{{ veeMacConfig.username }}</td>
							</tr>
							<tr>
								<td><strong>Password</strong></td>
								<td>••••••••••••</td>
							</tr>
							<tr>
								<td><strong>Last Synchronization</strong></td>
								<td>{{ veeMacConfig.lastSync }}</td>
							</tr>
							<tr>
								<td><strong>Synchronization Status</strong></td>
								<td>
									<span class="status-inactive">
										<i class="bi bi-x-circle-fill me-1"></i> Inactive (Manual
										sync only)
									</span>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<div class="d-flex gap-2">
					<button class="btn btn-success" @click="openVeeMacModal">
						<i class="bi bi-pencil-square me-2"></i>Edit Credentials
					</button>
					<button
						class="btn btn-outline-danger"
						@click="removeIntegration('veemac')"
					>
						<i class="bi bi-trash me-2"></i>Remove Integration
					</button>
				</div>
			</div>
		</div>
	</div>

	<!-- VeeMac Edit Credentials Modal -->
	<div
		class="modal fade"
		ref="veemacModalEl"
		tabindex="-1"
		aria-labelledby="veemacModalLabel"
		aria-hidden="true"
	>
		<div class="modal-dialog">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title" id="veemacModalLabel">
						Edit VeeMac Credentials
					</h5>
					<button
						type="button"
						class="btn-close"
						@click="closeVeeMacModal"
						aria-label="Close"
					></button>
				</div>
				<div class="modal-body">
					<form @submit.prevent="saveVeeMacConfig">
						<div class="mb-3">
							<label for="veemacUsername" class="form-label">Username</label>
							<input
								type="text"
								class="form-control"
								id="veemacUsername"
								v-model="veeMacForm.username"
							/>
						</div>
						<div class="mb-3">
							<label for="veemacPassword" class="form-label">Password</label>
							<input
								type="password"
								class="form-control"
								id="veemacPassword"
								v-model="veeMacForm.password"
							/>
						</div>
						<div class="mb-3 form-check">
							<input
								type="checkbox"
								class="form-check-input"
								id="veemacSyncCheck"
								v-model="veeMacForm.autoSync"
								@change="handleVeeMacSyncChange"
							/>
							<label class="form-check-label" for="veemacSyncCheck">
								Enable automatic synchronization
							</label>
						</div>
						<div class="mb-3">
							<label for="veemacSyncFrequency" class="form-label"
								>Sync Frequency</label
							>
							<select
								class="form-select"
								id="veemacSyncFrequency"
								v-model="veeMacForm.syncFrequency"
								:disabled="!veeMacForm.autoSync"
							>
								<option value="daily">Daily</option>
								<option value="weekly">Weekly</option>
								<option value="hourly">Hourly</option>
							</select>
						</div>
					</form>
				</div>
				<div class="modal-footer">
					<button
						type="button"
						class="btn btn-secondary"
						@click="closeVeeMacModal"
					>
						Cancel
					</button>
					<button
						type="button"
						class="btn btn-danger"
						@click="saveVeeMacConfig"
					>
						Save Changes
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onUnmounted } from "vue";
import { Modal } from "bootstrap";

interface VeeMacConfig {
	username: string;
	lastSync: string;
}

interface VeeMacForm {
	username: string;
	password: string;
	autoSync: boolean;
	syncFrequency: string;
}

const veemacModalEl = ref<HTMLElement | null>(null);
let veemacModal: Modal | null = null;

const veeMacConfig = reactive<VeeMacConfig>({
	username: "mosquito_district21",
	lastSync: "December 6, 2025 at 11:15 PM (Yesterday)",
});

const veeMacForm = reactive<VeeMacForm>({
	username: "mosquito_district21",
	password: "password123",
	autoSync: false,
	syncFrequency: "daily",
});

onMounted(() => {
	if (veemacModalEl.value) {
		veemacModal = new Modal(veemacModalEl.value);
	}
	loadIntegrations();
});

onUnmounted(() => {
	veemacModal?.dispose();
});

const loadIntegrations = async (): Promise<void> => {
	// TODO: Fetch data from API
};

const openVeeMacModal = (): void => {
	veemacModal?.show();
};

const closeVeeMacModal = (): void => {
	veemacModal?.hide();
};

const saveVeeMacConfig = async (): Promise<void> => {
	try {
		// TODO: Send data to API
		console.log("Saving VeeMac config:", veeMacForm);
		closeVeeMacModal();
		await loadIntegrations();
	} catch (error) {
		console.error("Error saving VeeMac config:", error);
	}
};

const handleVeeMacSyncChange = (): void => {
	if (!veeMacForm.autoSync) {
		veeMacForm.syncFrequency = "daily";
	}
};

const removeIntegration = async (integration: string): Promise<void> => {
	if (
		!confirm(`Are you sure you want to remove the ${integration} integration?`)
	) {
		return;
	}

	try {
		// TODO: Send delete request to API
		console.log(`Removing ${integration} integration`);
		await loadIntegrations();
	} catch (error) {
		console.error(`Error removing ${integration} integration:`, error);
	}
};
</script>

<style scoped>
.integration-card {
	border-left: 5px solid #dc3545;
}

.status-inactive {
	color: #dc3545;
}
</style>
